/**
 * The data picker: a popover under an input that lists what earlier nodes
 * produce, as a tree of fields, and inserts an expression for the one you
 * click. The previous step's fields are `$json.x`; anything further back is
 * `$node("Name").first.x`; variables are `$vars.x`.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@/shared/components/icons'
import { NodeIcon } from '../canvas/icons'
import type { NodeSpec } from '../types'
import { pathExpression, previewOf, type FieldNode } from './schema'
import type { Source } from './useSources'

interface Props {
  sources: Source[]
  specs: Record<string, NodeSpec>
  onInsert: (expression: string) => void
  onClose: () => void
}

export function DataPicker({ sources, specs, onInsert, onClose }: Props) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const host = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => input.current?.focus(), [])
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement
      // The {x} button that opened us toggles; let its own click close us.
      if (el.closest?.('.wf-map-btn')) return
      if (host.current && !host.current.contains(el)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [onClose])

  const needle = q.trim().toLowerCase()
  const visible = useMemo(() => {
    if (!needle) return sources
    return sources
      .map((s) => ({ ...s, fields: filterFields(s.fields, needle) }))
      .filter((s) => s.fields.length)
  }, [sources, needle])

  const isOpen = (key: string, dflt: boolean) => open[key] ?? dflt

  return (
    <div className="wf-datapicker" ref={host} role="dialog" aria-label="Insert a value">
      <div className="wf-datapicker-head">
        <Icon.search />
        <input ref={input} value={q} placeholder="Find a field…" onChange={(e) => setQ(e.target.value)} aria-label="Find a field" />
      </div>
      <div className="wf-datapicker-body">
        {visible.length === 0 && (
          <p className="muted small" style={{ padding: 12 }}>
            {sources.length === 0 ? 'Nothing runs before this node yet. Wire a step in front of it first.' : 'No field matches.'}
          </p>
        )}
        {visible.map((s, i) => {
          const spec = s.node ? specs[s.node.type] : undefined
          const expanded = isOpen(s.key, Boolean(needle) || i === 0 || s.direct)
          return (
            <section key={s.key} className="wf-datapicker-source" data-direct={s.direct || undefined}>
              <button type="button" className="wf-datapicker-sourcehead" onClick={() => setOpen((o) => ({ ...o, [s.key]: !expanded }))} aria-expanded={expanded}>
                <span className="wf-datapicker-caret" data-open={expanded || undefined}>▸</span>
                <span className="wf-node-icon" data-group={spec?.group ?? (s.key === '$vars' ? 'data' : 'output')}>
                  <NodeIcon name={spec?.icon ?? (s.key === '$vars' ? 'edit' : s.key === '$loop' ? 'loop' : 'play')} />
                </span>
                <span className="wf-datapicker-sourcetext">
                  <span className="wf-datapicker-sourcetitle">{s.title}</span>
                  <span className="wf-datapicker-sourcesub">
                    {s.subtitle}
                    {s.fromRun ? ' · from last run' : s.loading ? ' · loading shape…' : ''}
                  </span>
                </span>
                <code className="wf-datapicker-root">{s.root || '$'}</code>
              </button>
              {expanded && (
                <div className="wf-datapicker-fields">
                  {s.fields.length === 0 && (
                    <p className="muted small wf-datapicker-nofields">
                      {s.loading ? 'Loading…' : s.key === '$vars' ? 'No variables yet — add them under Workflow settings.' : 'Shape unknown — run the workflow, or “Test this step” on it, to see its fields.'}
                    </p>
                  )}
                  {s.fields.map((f) => (
                    <FieldRow key={f.path} field={f} root={s.root} depth={0} forceOpen={Boolean(needle)} onInsert={onInsert} />
                  ))}
                  {s.node && s.direct && (
                    <button type="button" className="wf-datapicker-field wf-datapicker-whole" onClick={() => onInsert('{{ $json }}')}>
                      <span className="wf-datapicker-key">the whole item</span>
                      <code className="wf-datapicker-expr">$json</code>
                    </button>
                  )}
                  {s.node && !s.direct && (
                    <button type="button" className="wf-datapicker-field wf-datapicker-whole" onClick={() => onInsert(`{{ $node(${JSON.stringify(s.node!.name)}).all }}`)}>
                      <span className="wf-datapicker-key">all of its items</span>
                      <code className="wf-datapicker-expr">.all</code>
                    </button>
                  )}
                </div>
              )}
            </section>
          )
        })}
      </div>
      <div className="wf-datapicker-foot small muted">
        Click a field to insert it. Expressions are <code>{'{{ … }}'}</code>; edit them freely afterwards.
      </div>
    </div>
  )
}

function FieldRow({ field, root, depth, forceOpen, onInsert }: { field: FieldNode; root: string; depth: number; forceOpen: boolean; onInsert: (e: string) => void }) {
  const [open, setOpen] = useState(forceOpen || depth < 1)
  const hasChildren = Boolean(field.children?.length)
  const expr = root ? pathExpression(root, field.path) : field.path
  const preview = previewOf(field.sample)
  return (
    <>
      <div className="wf-datapicker-field" style={{ paddingLeft: 10 + depth * 14 }}>
        {hasChildren ? (
          <button type="button" className="wf-datapicker-caret" data-open={open || undefined} onClick={() => setOpen((v) => !v)} aria-label={open ? 'Collapse' : 'Expand'}>▸</button>
        ) : (
          <span className="wf-datapicker-caret wf-datapicker-caret-none" />
        )}
        <button type="button" className="wf-datapicker-pick" onClick={() => onInsert(`{{ ${expr} }}`)} title={field.description || expr}>
          <span className="wf-datapicker-key">{field.key}</span>
          <span className="wf-datapicker-type" data-type={field.type}>{field.type}</span>
          {preview && <span className="wf-datapicker-preview">{preview}</span>}
        </button>
      </div>
      {hasChildren && open && field.children!.map((c) => <FieldRow key={c.path} field={c} root={root} depth={depth + 1} forceOpen={forceOpen} onInsert={onInsert} />)}
    </>
  )
}

function filterFields(fields: FieldNode[], needle: string): FieldNode[] {
  const out: FieldNode[] = []
  for (const f of fields) {
    const kids = f.children ? filterFields(f.children, needle) : []
    if (f.key.toLowerCase().includes(needle) || f.path.toLowerCase().includes(needle) || kids.length) {
      out.push({ ...f, children: kids.length ? kids : f.children })
    }
  }
  return out
}
